import React from 'react';
import { Button } from '@/components/ui/button';
import { useConnectionStatus } from '@/hooks/useConnectionStatus';
import { UserPlus, Clock, Check, UserMinus, UserCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CustomDropdownMenu } from '@/components/ui/CustomDropdownMenu';
import { supabase } from '@/integrations/supabase/client';
import { useI18n } from '@/contexts/I18nContext';

interface ConnectionButtonProps {
  targetUserId: string;
  className?: string;
  onConnectionChange?: () => void;
}

export const ConnectionButton: React.FC<ConnectionButtonProps> = ({ 
  targetUserId, 
  className,
  onConnectionChange 
}) => {
  const { status, loading, error, sendRequest, refetch } = useConnectionStatus(targetUserId);
  const { t } = useI18n();

  const handleDisconnect = async () => {
    try {
      const { error } = await supabase.rpc('remove_connection', { target_user: targetUserId });
      if (error) {
        console.error('Erro ao remover conexão:', error);
        return;
      }
      await refetch();
      if (onConnectionChange) {
        onConnectionChange();
      }
    } catch (error) {
      console.error('Erro ao processar remoção de conexão:', error);
    }
  };

  if (status === 'connected') {
    return (
      <div className="w-full">
        <CustomDropdownMenu
          trigger={
            <Button
              disabled
              className={cn(
                className,
                'rounded-full w-full text-white bg-[#7F7F7F] py-1.5 flex items-center justify-center min-w-full',
                '!border-0 !text-white !bg-[#7F7F7F] !hover:bg-[#7F7F7F] !active:bg-[#7F7F7F] !focus:bg-[#7F7F7F]'
              )}
              variant="outline"
              style={{ width: '100%', minWidth: '100%' }}
            >
              {t('connection.connected')}
            </Button>
          }
          items={[{
            label: t('connection.disconnect'),
            icon: <UserMinus className="h-4 w-4 mr-2 text-white" />,
            onClick: handleDisconnect,
            className: "text-white hover:text-white"
          }]}
        />
      </div>
    );
  }
  
  if (status === 'pending') {
    return (
      <div className="w-full">
        <Button
          disabled
          className={cn(className, 'rounded-full border-0 text-white bg-[#4A403E] w-full py-1.5 hover:bg-[#4A403E]/90')}
          variant="outline"
          style={{boxShadow: 'none'}}
        >
          {t('connection.requested')}
        </Button>
      </div>
    );
  }
  
  // status === 'none'
  return (
    <div className="w-full">
      <Button
        onClick={sendRequest}
        disabled={loading}
        className={cn(className, 'rounded-full border-0 text-white bg-[#D72E16] w-full py-1.5 hover:bg-[#D72E16]/90')}
        variant="outline"
        style={{boxShadow: 'none'}}
      >
        {loading ? t('connection.sending') : t('connection.connect')}
      </Button>
    </div>
  );
}; 